import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setCrmStageProbability, type CrmDealStage } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import styles from './Crm.module.css';

/**
 * A stage's chance of winning ("30%") in its column header; click to type a
 * new one, Enter or leaving the field saves, Escape cancels.
 */
export const CrmStageRate = ({
  stage,
  value,
}: {
  stage: CrmDealStage;
  value: number;
}) => {
  const { t } = useCrmFormat();
  const client = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const label = t('crm.workspace.win_rate_named', {
    stage: t(`crm.workspace.stages.${stage}`),
  });
  const save = async () => {
    if (draft === null) return;
    const next = Math.round(Number(draft));
    setDraft(null);
    if (!Number.isFinite(next) || next < 0 || next > 100 || next === value)
      return;
    setSaving(true);
    try {
      client.setQueryData(
        ['crm', 'stages'],
        await setCrmStageProbability(stage, next)
      );
      await client.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
    } finally {
      setSaving(false);
    }
  };
  return draft === null ? (
    <button
      type="button"
      className={styles.stageRate}
      aria-label={label}
      title={label}
      disabled={saving}
      onClick={() => setDraft(String(value))}
    >
      {value}%
    </button>
  ) : (
    <input
      autoFocus
      type="number"
      min={0}
      max={100}
      step={1}
      aria-label={label}
      className={styles.stageRateInput}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void save()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') void save();
        if (event.key === 'Escape') setDraft(null);
      }}
    />
  );
};
